import type { MedicalNoteFieldConfig, MedicalNoteSectionConfig } from "types";
import { MedicalNoteReadonlyFieldTypes } from "types";
import type { CareEventFieldPath } from "types/MedicalNoteConfig";

const getFieldNamesFromConfig =
  (editableOnly: boolean) =>
  (config: MedicalNoteSectionConfig[], isEditingDateEnabled: boolean): CareEventFieldPath[] =>
    config
      .flatMap(em => em.fields || [])
      .filter(field => !editableOnly || !MedicalNoteReadonlyFieldTypes.some(fieldType => fieldType === field.type))
      // single checkbox in UI updates two fields in backend because reasons
      .flatMap(({ name }) => {
        if (name !== "confirmed_insurance_and_identity") return name;
        return ["confirmed_identity", "confirmed_insurance"] as const;
      })
      .concat(isEditingDateEnabled ? ["start_time"] : [])
      .concat(config.some(section => section.type === "exclusion") ? ["structured_fields.exclusion_reason"] : []);

const getEditableFieldNamesFromConfig = getFieldNamesFromConfig(true);
const getAllFieldNamesFromConfig = getFieldNamesFromConfig(false);

/**
 * Checks if a section should be visible based on its condition.
 * @param section - The section configuration to check
 * @param getFieldValue - Function to get the current value of a field by name
 * @returns true if the section should be shown, false otherwise
 */
const shouldShowSection = (
  section: MedicalNoteSectionConfig,
  getFieldValue: (fieldName: string) => unknown
): boolean => {
  if (!section.condition) return true;

  const fieldValue = getFieldValue(section.condition.field);
  const matches = fieldValue === section.condition.value;

  // If show is true, show when matches. If show is false, show when NOT matches
  return section.condition.show ? matches : !matches;
};

/**
 * Checks if a field should be visible based on its condition.
 * @param field - The field configuration to check
 * @param getFieldValue - Function to get the current value of a field by name
 * @returns true if the field should be shown, false otherwise
 */
const shouldShowField = (field: MedicalNoteFieldConfig, getFieldValue: (fieldName: string) => unknown): boolean => {
  if (!field.condition) return true;

  const fieldValue = getFieldValue(field.condition.field);
  const matches = fieldValue === field.condition.value;

  // If show is true, show when matches. If show is false, show when NOT matches
  return field.condition.show ? matches : !matches;
};

export { getEditableFieldNamesFromConfig, getAllFieldNamesFromConfig, shouldShowSection, shouldShowField };
